import {
  filterDevices,
  getDeviceFilter,
  getOperationalState,
  getPlatformFilter,
  getStateFilter,
  setDeviceFilter,
  setPlatformFilter,
  setStateFilter,
} from "../utils/deviceFilterContext";
import { getSelectedCompanyId } from "../utils/tenantContext";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiJson } from "../api/client";
import "./OverviewV2Page.css";
import NetworkInspectionPanel from "./NetworkInspectionPanel";

const REFRESH_MS = 30000;

function asArray(value, key) {
  if (Array.isArray(value)) return value;

  if (
    value &&
    key &&
    Array.isArray(value[key])
  ) {
    return value[key];
  }

  return [];
}

function normalizePlatform(value) {
  const platform = String(
    value || ""
  ).trim().toLowerCase();

  if (
    platform === "darwin" ||
    platform === "macos" ||
    platform.startsWith("macos") ||
    platform.startsWith("mac os")
  ) {
    return "macOS";
  }

  if (
    platform === "windows" ||
    platform.startsWith("windows")
  ) {
    return "Windows";
  }

  return "Other";
}

function statusClass(status) {
  const value = String(
    status || ""
  ).toLowerCase();

  if (value === "online") {
    return "ov2-status-online";
  }

  if (value === "offline") {
    return "ov2-status-offline";
  }

  if (value === "stale") {
    return "ov2-status-stale";
  }

  return "ov2-status-neutral";
}

function formatAge(seconds) {
  const value = Number(seconds);

  if (!Number.isFinite(value)) {
    return "No heartbeat";
  }

  if (value < 60) {
    return `${Math.round(value)} sec`;
  }

  if (value < 3600) {
    return `${Math.round(value / 60)} min`;
  }

  if (value < 86400) {
    return `${Math.round(value / 3600)} hr`;
  }

  return `${Math.round(value / 86400)} d`;
}

function Metric({
  value,
  label,
  detail,
  tone = "blue",
}) {
  return (
    <div className={`ov2-metric ov2-${tone}`}>
      <span className="ov2-signal" />

      <strong>{value}</strong>

      <span>{label}</span>

      <small>{detail}</small>
    </div>
  );
}

function Donut({
  value,
  label,
}) {
  const safe = Math.max(
    0,
    Math.min(100, Number(value) || 0)
  );

  return (
    <div className="ov2-donut-wrap">
      <div
        className="ov2-donut"
        style={{
          "--progress": `${safe * 3.6}deg`,
        }}
      >
        <div className="ov2-donut-core">
          <strong>{safe}%</strong>
        </div>
      </div>

      <span>{label}</span>
    </div>
  );
}

function PlatformCard({
  platform,
  devices,
  inventory,
  runtime,
}) {
  const platformDevices =
    devices.filter(
      (device) =>
        normalizePlatform(
          device.platform
        ) === platform &&
        device.lifecycle_state === "active"
    );

  const online =
    platformDevices.filter(
      (device) =>
        device.effective_status === "online"
    ).length;

  const offline =
    platformDevices.filter(
      (device) =>
        device.effective_status === "offline"
    ).length;

  const stale =
    platformDevices.filter(
      (device) =>
        device.effective_status === "stale"
    ).length;

  const uuids =
    new Set(
      platformDevices.map(
        (device) => device.device_uuid
      )
    );

  const inventoried =
    inventory.filter(
      (item) =>
        uuids.has(item.device_uuid) &&
        item.observed_at
    ).length;

  const reporting =
    runtime.filter(
      (item) =>
        uuids.has(item.device_uuid) &&
        item.observed_at
    ).length;

  const coverage =
    platformDevices.length > 0
      ? Math.round(
          (reporting /
            platformDevices.length) *
            100
        )
      : 0;

  return (
    <section className="ov2-platform">
      <div className="ov2-platform-head">
        <div>
          <span>{platform}</span>
          <strong>
            {platformDevices.length}
          </strong>
        </div>

        <Donut
          value={coverage}
          label="Telemetry"
        />
      </div>

      <div className="ov2-platform-status">
        <div>
          <span className="ov2-dot ov2-dot-online" />
          <strong>{online}</strong>
          <small>Online</small>
        </div>

        <div>
          <span className="ov2-dot ov2-dot-offline" />
          <strong>{offline}</strong>
          <small>Offline</small>
        </div>

        <div>
          <span className="ov2-dot ov2-dot-stale" />
          <strong>{stale}</strong>
          <small>Stale</small>
        </div>
      </div>

      <div className="ov2-coverage-row">
        <span>Inventory</span>

        <div>
          <i
            style={{
              width:
                platformDevices.length
                  ? `${Math.round(
                      (inventoried /
                        platformDevices.length) *
                        100
                    )}%`
                  : "0%",
            }}
          />
        </div>

        <strong>
          {inventoried}/
          {platformDevices.length}
        </strong>
      </div>

      <div className="ov2-coverage-row">
        <span>Runtime</span>

        <div>
          <i
            style={{
              width:
                platformDevices.length
                  ? `${Math.round(
                      (reporting /
                        platformDevices.length) *
                        100
                    )}%`
                  : "0%",
            }}
          />
        </div>

        <strong>
          {reporting}/
          {platformDevices.length}
        </strong>
      </div>

      <div className="ov2-device-strip">
        {platformDevices.map(
          (device) => (
            <div
              key={device.device_uuid}
              className="ov2-device-row"
            >
              <span
                className={`ov2-device-state ${statusClass(
                  device.effective_status
                )}`}
              />

              <div>
                <strong>
                  {device.device_name ||
                    device.hostname ||
                    device.device_uuid}
                </strong>

                <small>
                  {device.status_reason ||
                    "status unavailable"}
                </small>
              </div>

              <span>
                {formatAge(
                  device.heartbeat_age_seconds
                )}
              </span>
            </div>
          )
        )}
      </div>
    </section>
  );
}

function BarSet({
  rows,
}) {
  const max = Math.max(
    ...rows.map((row) => row.value),
    1
  );

  return (
    <div className="ov2-bars">
      {rows.map((row) => (
        <div
          key={row.label}
          className="ov2-bar-row"
        >
          <div>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>

          <div className="ov2-bar-track">
            <i
              className={`ov2-bar-fill ov2-fill-${row.tone}`}
              style={{
                width:
                  row.value > 0
                    ? `${Math.max(
                        4,
                        Math.round(
                          (row.value / max) *
                            100
                        )
                      )}%`
                    : "0%",
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function OverviewV2Page() {

  // WIRELESSPRESS_R3_FILTER_STATE
  const [platformFilter, setPlatformFilterState] =
    React.useState(getPlatformFilter());

  const [stateFilter, setStateFilterState] =
    React.useState(getStateFilter());

  const [deviceFilter, setDeviceFilterState] =
    React.useState(getDeviceFilter());

  React.useEffect(() => {
    const handler = () => {
      setPlatformFilterState(getPlatformFilter());
      setStateFilterState(getStateFilter());
      setDeviceFilterState(getDeviceFilter());
    };

    window.addEventListener(
      "wirelesspress-device-filter-change",
      handler
    );

    return () =>
      window.removeEventListener(
        "wirelesspress-device-filter-change",
        handler
      );
  }, []);

  // WIRELESSPRESS_R3_FILTER_HELPERS
  const applyR3DeviceFilters = React.useCallback(
    (devices) =>
      filterDevices(devices, {
        platform: platformFilter,
        state: stateFilter,
        deviceUuid: deviceFilter,
      }),
    [platformFilter, stateFilter, deviceFilter]
  );


  const [state, setState] =
    useState({
      status: [],
      inventory: [],
      runtime: [],
      vulnerabilities: [],
    });

  const [health, setHealth] =
    useState({});

  const [loading, setLoading] =
    useState(true);

  const [updated, setUpdated] =
    useState(null);

  const load = useCallback(
    async () => {
      const companyId =
        getSelectedCompanyId();

      const suffix =
        companyId
          ? `?company_id=${encodeURIComponent(
              companyId
            )}`
          : "";

      const requests = [
        [
          "status",
          `/device-status/devices${suffix}`,
        ],
        [
          "inventory",
          `/endpoint-inventory/devices${suffix}`,
        ],
        [
          "runtime",
          `/runtime-telemetry/devices${suffix}`,
        ],
        [
          "vulnerabilities",
          companyId
            ? `/vulnerabilities/company/${companyId}`
            : "/vulnerabilities",
        ],
      ];

      const results =
        await Promise.all(
          requests.map(
            async ([key, path]) => {
              try {
                return {
                  key,
                  ok: true,
                  data:
                    await apiJson(path),
                };
              } catch (error) {
                console.error(
                  `Overview V2 ${key}:`,
                  error
                );

                return {
                  key,
                  ok: false,
                  data: null,
                };
              }
            }
          )
        );

      const values = {};
      const nextHealth = {};

      results.forEach(
        ({ key, ok, data }) => {
          values[key] = data;
          nextHealth[key] = ok;
        }
      );

      setState({
        status: asArray(
          values.status
        ),
        inventory: asArray(
          values.inventory
        ),
        runtime: asArray(
          values.runtime
        ),
        vulnerabilities:
          asArray(
            values.vulnerabilities,
            "vulnerabilities"
          ),
      });

      setHealth(nextHealth);
      setUpdated(new Date());
      setLoading(false);
    },
    []
  );

  useEffect(() => {
    load();

    const timer =
      window.setInterval(
        load,
        REFRESH_MS
      );

    return () =>
      window.clearInterval(timer);
  }, [load]);

  const {
    status,
    inventory,
    runtime,
    vulnerabilities,
  } = state;


  // WIRELESSPRESS_R3_AUTHORITATIVE_STATUS_FILTER
  const filteredStatus =
    useMemo(
      () =>
        applyR3DeviceFilters(status),
      [status, applyR3DeviceFilters]
    );

  const selectableDevices =
    useMemo(
      () =>
        filterDevices(status, {
          platform: platformFilter,
          state: stateFilter,
          deviceUuid: "",
        }),
      [status, platformFilter, stateFilter]
    );

  const active =
    filteredStatus.filter(
      (device) =>
        device.lifecycle_state ===
        "active"
    );

  const online =
    active.filter(
      (device) =>
        device.effective_status ===
        "online"
    ).length;

  const offline =
    active.filter(
      (device) =>
        device.effective_status ===
        "offline"
    ).length;

  const stale =
    active.filter(
      (device) =>
        device.effective_status ===
        "stale"
    ).length;

  const reporting =
    runtime.filter(
      (item) =>
        item.observed_at &&
        active.some(
          (device) =>
            device.device_uuid ===
            item.device_uuid
        )
    );

  const runtimeTotals =
    reporting.reduce(
      (totals, item) => ({
        processes:
          totals.processes +
          Number(
            item.process_count || 0
          ),
        services:
          totals.services +
          Number(
            item.service_count || 0
          ),
        connections:
          totals.connections +
          Number(
            item.network_connection_count ||
              0
          ),
      }),
      {
        processes: 0,
        services: 0,
        connections: 0,
      }
    );

  const vulnerabilityRows =
    useMemo(
      () =>
        [
          "critical",
          "high",
          "medium",
          "low",
        ].map((severity) => ({
          label:
            severity[0].toUpperCase() +
            severity.slice(1),
          tone: severity,
          value:
            vulnerabilities.filter(
              (item) =>
                String(
                  item.severity || ""
                ).toLowerCase() ===
                severity
            ).length,
        })),
      [vulnerabilities]
    );

  const collectionCoverage =
    active.length > 0
      ? Math.round(
          (reporting.length /
            active.length) *
            100
        )
      : 0;

  if (loading) {
    return (
      <div className="ov2-loading">
        <div />
      </div>
    );
  }

  return (
    <div className="ov2-page">
      <header className="ov2-header">
        <div>
          <h1>Overview V2</h1>

          <span>
            <i />
            Live endpoint posture
          </span>
        </div>

        <div className="ov2-update">
          {updated
            ? updated.toLocaleTimeString(
                [],
                {
                  hour: "2-digit",
                  minute: "2-digit",
                }
              )
            : "--"}
        </div>
      </header>

      {/* WIRELESSPRESS_R3_FILTER_TOOLBAR */}
      <section className="ov2-filterbar">

        <div className="ov2-filtergroup">
          <span>Platform</span>

          <button
            type="button"
            className={
              platformFilter === "all"
                ? "active"
                : ""
            }
            onClick={() =>
              setPlatformFilter("all")
            }
          >
            All
          </button>

          <button
            type="button"
            className={
              platformFilter === "windows"
                ? "active"
                : ""
            }
            onClick={() =>
              setPlatformFilter("windows")
            }
          >
            Windows
          </button>

          <button
            type="button"
            className={
              platformFilter === "macos"
                ? "active"
                : ""
            }
            onClick={() =>
              setPlatformFilter("macos")
            }
          >
            macOS
          </button>
        </div>

        <div className="ov2-filtergroup">
          <span>State</span>

          <button
            type="button"
            className={
              stateFilter === "current"
                ? "active"
                : ""
            }
            onClick={() =>
              setStateFilter("current")
            }
          >
            Current
          </button>

          <button
            type="button"
            className={
              stateFilter === "offline"
                ? "active"
                : ""
            }
            onClick={() =>
              setStateFilter("offline")
            }
          >
            Offline
          </button>

          <button
            type="button"
            className={
              stateFilter === "stale"
                ? "active"
                : ""
            }
            onClick={() =>
              setStateFilter("stale")
            }
          >
            Stale
          </button>

          <button
            type="button"
            className={
              stateFilter === "decommissioned"
                ? "active"
                : ""
            }
            onClick={() =>
              setStateFilter(
                "decommissioned"
              )
            }
          >
            Decommissioned
          </button>

          <button
            type="button"
            className={
              stateFilter === "all"
                ? "active"
                : ""
            }
            onClick={() =>
              setStateFilter("all")
            }
          >
            All
          </button>
        </div>

        <div className="ov2-filtergroup ov2-device-filter">
          <span>Device</span>

          <select
            value={deviceFilter}
            onChange={(event) =>
              setDeviceFilter(
                event.target.value
              )
            }
          >
            <option value="">
              All devices
            </option>

            {selectableDevices.map(
              (device) => (
                <option
                  key={
                    device.device_uuid ||
                    device.id
                  }
                  value={
                    device.device_uuid ||
                    ""
                  }
                >
                  {device.device_name ||
                    device.hostname ||
                    device.device_uuid}
                </option>
              )
            )}
          </select>
        </div>

        <div className="ov2-filter-result">
          <span>Showing</span>
          <strong>
            {filteredStatus.length}
          </strong>
          <span>
            endpoint
            {filteredStatus.length === 1
              ? ""
              : "s"}
          </span>
        </div>

      </section>


      <section className="ov2-metrics">
        <Metric
          value={active.length}
          label="Active endpoints"
          detail="Current lifecycle"
          tone="blue"
        />

        <Metric
          value={online}
          label="Online"
          detail="Fresh heartbeat"
          tone="green"
        />

        <Metric
          value={offline}
          label="Offline"
          detail="Heartbeat late"
          tone={
            offline > 0
              ? "orange"
              : "green"
          }
        />

        <Metric
          value={stale}
          label="Stale"
          detail="Long inactive"
          tone={
            stale > 0
              ? "red"
              : "green"
          }
        />

        <Metric
          value={`${collectionCoverage}%`}
          label="Telemetry"
          detail={`${reporting.length}/${active.length} reporting`}
          tone="purple"
        />
      </section>

            <NetworkInspectionPanel />

      <section className="ov2-platform-grid">
        <PlatformCard
          platform="Windows"
          devices={filteredStatus}
          inventory={inventory}
          runtime={runtime}
        />

        <PlatformCard
          platform="macOS"
          devices={filteredStatus}
          inventory={inventory}
          runtime={runtime}
        />
      </section>

      <section className="ov2-lower-grid">
        <article className="ov2-panel">
          <div className="ov2-panel-head">
            <div>
              <span>
                RUNTIME COLLECTION
              </span>
              <strong>
                Current Signal
              </strong>
            </div>

            <b>
              {reporting.length}
            </b>
          </div>

          <BarSet
            rows={[
              {
                label: "Processes",
                value:
                  runtimeTotals.processes,
                tone: "blue",
              },
              {
                label: "Services",
                value:
                  runtimeTotals.services,
                tone: "green",
              },
              {
                label: "Connections",
                value:
                  runtimeTotals.connections,
                tone: "purple",
              },
            ]}
          />
        </article>

        <article className="ov2-panel">
          <div className="ov2-panel-head">
            <div>
              <span>
                VULNERABILITY
              </span>
              <strong>
                Exposure
              </strong>
            </div>

            <b>
              {vulnerabilities.length}
            </b>
          </div>

          <BarSet
            rows={
              vulnerabilityRows
            }
          />
        </article>

        <article className="ov2-panel ov2-health-panel">
          <div className="ov2-panel-head">
            <div>
              <span>
                PLATFORM
              </span>
              <strong>
                Data Sources
              </strong>
            </div>
          </div>

          {[
            [
              "Status",
              health.status,
            ],
            [
              "Inventory",
              health.inventory,
            ],
            [
              "Runtime",
              health.runtime,
            ],
            [
              "Vulnerability",
              health.vulnerabilities,
            ],
          ].map(
            ([label, ok]) => (
              <div
                key={label}
                className="ov2-health-row"
              >
                <span
                  className={
                    ok
                      ? "ov2-health-up"
                      : "ov2-health-down"
                  }
                />

                <strong>
                  {label}
                </strong>

                <small>
                  {ok ? "Live" : "Unavailable"}
                </small>
              </div>
            )
          )}
        </article>
      </section>
    </div>
  );
}

export default OverviewV2Page;
